import {
  Checkbox,
  DatePicker,
  Grid,
  GridColumn,
  GridRow,
  Input,
  LazyLoader,
  ModalBottomButtons,
  Select,
  SelectOptionType,
} from '@bp/ui-components';
import dayjs from 'dayjs';
import { Form, Formik, FormikHelpers } from 'formik';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiValue, SingleValue } from 'react-select';
import { rrulestr } from 'rrule';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag, useMemorizedCacheTags } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { SchoolEventType } from '../../../pages/Substitutions/Plan/PlanSchoolEvent/PlanSchoolEvent';
import {
  EventClassOrGroupType,
  use_CreateRecurringSchoolEventMutation,
  use_CreateSingleSchoolEventMutation,
  use_EventByUuidQuery,
  use_RecurringEventQuery,
  use_UpdateRecurrenceSchoolEventsMutation,
  use_UpdateSchoolEventMutation,
} from '../../../types/planung-graphql-client-defs';
import {
  combineFirstDateWithSecondTime,
  getDaysDifference,
  setTimeStringOnDate,
} from '../../../utils/dateCalculations';
import { lowercaseFirstLetter } from '../../../utils/stringHelper';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserErrorToast } from '../../../utils/toast';
import type { SchoolEventEditScope } from '../Modals/SchoolEventModal';
import { RecurringEventForm } from './RecurringEventForm';
import { schoolEventSchema } from './schema';

export type SchoolEventFormProps = {
  eventUuid?: string | null;
  /** nur beim Bearbeiten eines Serientermins relevant */
  editScope?: SchoolEventEditScope;
  recurrenceUuid?: string | null;
  onLoading: (loading: boolean) => void;
  onClose: () => void;
};

export const SchoolEventForm = ({
  eventUuid,
  editScope = 'single',
  recurrenceUuid = null,
  onLoading,
  onClose,
}: SchoolEventFormProps) => {
  const { t } = useTranslation();
  const currentSchoolYear = useSelectedSchoolYear();

  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const schoolEventContext = useMemorizedCacheTag('SCHOOL_EVENT');

  const isEdit = !!eventUuid;
  const isSeriesEdit = isEdit && editScope === 'series' && !!recurrenceUuid;
  // Bearbeiten ändert Veranstaltungen und die betroffenen Unterrichtstermine
  const updateContext = useMemorizedCacheTags(['SCHOOL_EVENT', 'EVENT']);

  const [{ data: eventData }] = use_EventByUuidQuery({
    variables: { uuid: eventUuid ?? '' },
    context: schoolEventContext,
    pause: !isEdit,
    requestPolicy: 'network-only',
  });

  const [{ data: recurringData }] = use_RecurringEventQuery({
    variables: { uuid: recurrenceUuid ?? '' },
    pause: !isSeriesEdit,
    requestPolicy: 'network-only',
  });

  const [, createSingleEvent] = use_CreateSingleSchoolEventMutation();
  const [, createRecurringEvent] = use_CreateRecurringSchoolEventMutation();
  const [, updateSingleEvent] = use_UpdateSchoolEventMutation();
  const [, updateRecurringEvents] = use_UpdateRecurrenceSchoolEventsMutation();

  const currentEvent = eventData?.event;
  const recurringEvent = recurringData?.recurringEvent;
  const isLoadingEvent = (isEdit && !currentEvent) || (isSeriesEdit && !recurringEvent);

  const initialValues: SchoolEventType = useMemo(() => {
    const eventStart = currentEvent?.start ? dayjs(currentEvent.start) : dayjs().startOf('day');
    const eventEnd = currentEvent?.end ? dayjs(currentEvent.end) : dayjs().endOf('day');
    // Serie: Tag aus dem Serienstart der Regel, Uhrzeit aus dem angeklickten Termin
    const seriesStart = recurringEvent?.rrule ? rrulestr(recurringEvent.rrule).options.dtstart : null;
    const startDate = seriesStart
      ? eventStart.year(seriesStart.getFullYear()).month(seriesStart.getMonth()).date(seriesStart.getDate())
      : eventStart;
    const endDate = seriesStart ? startDate.hour(eventEnd.hour()).minute(eventEnd.minute()) : eventEnd;

    return {
      uuid: currentEvent?.uuid ?? '',
      name: currentEvent?.comment ?? '',
      classUuids:
        currentEvent?.classesOrGroups.filter((c) => c.type === EventClassOrGroupType.Class).map((c) => c.uuid) ?? [],
      groupUuids:
        currentEvent?.classesOrGroups.filter((c) => c.type === EventClassOrGroupType.Group).map((c) => c.uuid) ?? [],
      subjectUuid: currentEvent?.subjectUuid ?? '',
      teacherUuids: currentEvent?.persons.map((person) => person.uuid) ?? [],
      startDate,
      endDate,
      isClassbookRelevant: currentEvent?.isClassbookRelevant ?? false,
      rrule: isSeriesEdit ? (recurringEvent?.rrule ?? null) : null,
      excludeHolidays: isSeriesEdit ? (recurringEvent?.excludeHolidays ?? true) : true,
    };
  }, [
    isSeriesEdit,
    recurringEvent?.rrule,
    recurringEvent?.excludeHolidays,
    currentEvent?.classesOrGroups,
    currentEvent?.comment,
    currentEvent?.end,
    currentEvent?.isClassbookRelevant,
    currentEvent?.persons,
    currentEvent?.start,
    currentEvent?.subjectUuid,
    currentEvent?.uuid,
  ]);

  const { classesData, groupsData, divisionsData, teacherData, subjectData } = useLoadBasicData({ pause: false });

  const activeTeachers = teacherData?.people.filter((p) => p.active);

  const classesOptions = useCreateSelectOptions(classesData?.classes, 'uuid', 'name');
  const subjectOptions = useCreateSelectOptions(subjectData?.subjects, 'uuid', 'name');
  const teacherOptions = useCreateSelectOptions(activeTeachers, 'uuid', 'listName');

  const filteredGroupOptions: SelectOptionType[] = useMemo(() => {
    const options: SelectOptionType[] = [];
    const classes = classesData?.classes.filter((c) => selectedClasses.includes(c.uuid));

    classes?.forEach((c) => {
      const divisions = divisionsData?.divisions.filter((d) => c.divisions.map((d) => d.uuid).includes(d.uuid));
      const groups = groupsData?.groups.filter((g) =>
        divisions?.flatMap((d) => d.groups.map((gc) => gc.uuid)).includes(g.uuid),
      );
      groups?.forEach((g) => {
        options.push({
          label: `${g.name} (${c.name})`,
          value: g.uuid,
        });
      });
    });

    return options;
  }, [classesData?.classes, divisionsData?.divisions, groupsData?.groups, selectedClasses]);

  useEffect(() => {
    setSelectedClasses(initialValues.classUuids);
  }, [initialValues.classUuids]);

  async function handleEdit(values: SchoolEventType) {
    if (!eventUuid) return;
    const common = {
      start: values.startDate.toISOString(),
      end: values.endDate.toISOString(),
      comment: values.name,
      subject: values.subjectUuid,
      isClassbookRelevant: values.isClassbookRelevant,
    };

    if (isSeriesEdit && recurrenceUuid && values.rrule) {
      // Serie: connect enthält die vollständigen neuen Verbindungen
      const res = await updateRecurringEvents(
        {
          recurrenceUuid,
          rrule: values.rrule,
          excludeHolidays: values.excludeHolidays,
          update: {
            ...common,
            persons: { connect: values.teacherUuids.map((uuid) => ({ uuid })) },
            classes: { connect: values.classUuids },
            groups: { connect: values.groupUuids },
          },
        },
        updateContext,
      );
      if (res.error) showUserErrorToast({ error: res.error });
      else showSuccessUpdateToast();
      return;
    }

    const diff = (next: string[], previous: string[]) => ({
      connect: next,
      disconnect: previous.filter((uuid) => !next.includes(uuid)),
    });
    const res = await updateSingleEvent(
      {
        eventUuid,
        update: {
          ...common,
          persons: {
            connect: values.teacherUuids.map((uuid) => ({ uuid })),
            disconnect: initialValues.teacherUuids.filter((uuid) => !values.teacherUuids.includes(uuid)),
          },
          classes: diff(values.classUuids, initialValues.classUuids),
          groups: diff(values.groupUuids, initialValues.groupUuids),
        },
      },
      updateContext,
    );
    if (res.error) showUserErrorToast({ error: res.error });
    else showSuccessUpdateToast();
  }

  async function handleSubmit(values: SchoolEventType, formHelpers: FormikHelpers<SchoolEventType>) {
    onLoading(true);

    if (eventUuid) {
      await handleEdit(values);
      onLoading(false);
      onClose();
      return;
    }

    if (!values.rrule) {
      if (!eventUuid) {
        const res = await createSingleEvent(
          {
            input: {
              comment: values.name,
              start: values.startDate.toISOString(),
              end: values.endDate.toISOString(),
              classes: values.classUuids,
              groups: values.groupUuids,
              rooms: [],
              subject: values.subjectUuid,
              isSchoolEvent: true,
              persons: values.teacherUuids,
              isClassbookRelevant: values.isClassbookRelevant,
            },
          },
          schoolEventContext,
        );
        if (res.error) {
          showUserErrorToast({ error: res.error });
        } else {
          showSuccessCreateToast();
        }
      }
    } else {
      if (!eventUuid) {
        const res = await createRecurringEvent(
          {
            input: {
              start: values.startDate.toISOString(),
              end: values.endDate.toISOString(),
              comment: values.name,
              classes: values.classUuids,
              groups: values.groupUuids,
              rooms: [],
              subject: values.subjectUuid,
              isClassbookRelevant: values.isClassbookRelevant,
              persons: values.teacherUuids,
              rrule: values.rrule,
              excludeHolidays: values.excludeHolidays,
            },
          },
          schoolEventContext,
        );
        if (res.error) {
          showUserErrorToast({ error: res.error });
        } else {
          showSuccessCreateToast();
        }
      }
    }

    onLoading(false);
    onClose();
  }

  if (isLoadingEvent) {
    return <LazyLoader transparent forceHeight='40vh' />;
  }

  return (
    <Formik<SchoolEventType>
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schoolEventSchema}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {({ setFieldValue, values, isSubmitting, dirty, resetForm, errors }) => {
        const allowRecurring = values.startDate.isSame(values.endDate, 'day');
        return (
          <Form>
            <Grid useFormGap>
              <GridRow>
                <GridColumn width={10}>
                  <GridRow spacingBottom='s'>
                    <Input
                      name='name'
                      value={values.name}
                      label={t('common.name')}
                      onChange={async (e) => await setFieldValue('name', e.target.value)}
                      error={errors.name}
                    />
                  </GridRow>
                  <GridRow spacingTop='s' spacingBottom='s'>
                    <GridColumn width={6}>
                      <Select
                        name='subject'
                        isClearable
                        error={errors.subjectUuid}
                        isSearchable
                        menuPosition='fixed'
                        label={t('subject.title', { count: 1 })}
                        options={subjectOptions}
                        value={subjectOptions?.find((s) => s.value === values.subjectUuid)}
                        onChange={async (option) => {
                          const opt = option as SingleValue<SelectOptionType>;
                          await setFieldValue('subjectUuid', opt?.value ?? null);
                        }}
                        required
                      />
                    </GridColumn>
                    <GridColumn width={6}>
                      <Select
                        name='teachers'
                        isMulti
                        isClearable
                        isSearchable
                        menuPosition='fixed'
                        label={t('persons.title', { count: 1 })}
                        options={teacherOptions}
                        value={teacherOptions?.filter((t) => values.teacherUuids.includes(t.value as string))}
                        onChange={async (options) => {
                          const opt = options as MultiValue<SelectOptionType>;
                          await setFieldValue(
                            'teacherUuids',
                            opt.map((o) => o.value),
                          );
                        }}
                        required
                      />
                    </GridColumn>
                  </GridRow>
                  <GridRow spacingTop='s'>
                    <GridColumn width={6}>
                      <Select
                        name='classes'
                        isMulti
                        isSearchable
                        isClearable
                        menuPosition='fixed'
                        label={t('classes.title', { count: 1 })}
                        options={classesOptions}
                        value={classesOptions?.filter((c) => values.classUuids.includes(c.value as string))}
                        onChange={async (e) => {
                          const opt = e as MultiValue<SelectOptionType>;
                          await setFieldValue(
                            'classUuids',
                            opt.map((o) => o.value),
                          );
                          setSelectedClasses(opt.map((o) => o.value as string));
                        }}
                      />
                    </GridColumn>
                    <GridColumn width={6}>
                      <Select
                        name='groups'
                        isMulti
                        isSearchable
                        isClearable
                        menuPosition='fixed'
                        label={t('groups.title', { count: 1 })}
                        options={filteredGroupOptions}
                        value={filteredGroupOptions.filter((g) => values.groupUuids.includes(g.value as string))}
                        onChange={async (e) => {
                          const opt = e as MultiValue<SelectOptionType>;
                          await setFieldValue(
                            'groupUuids',
                            opt.map((o) => o.value),
                          );
                        }}
                        disabled={filteredGroupOptions.length === 0}
                        placeholder={lowercaseFirstLetter(t('schoolEvents.noClassesHint'))}
                      />
                    </GridColumn>
                  </GridRow>
                </GridColumn>
                <GridColumn width={2} align='end'>
                  <Checkbox
                    className='mt-3'
                    name='classbookRelevant'
                    label={t('schoolEvents.classbookRelevant')}
                    checked={values.isClassbookRelevant}
                    onChange={async (e) => await setFieldValue('isClassbookRelevant', e.target.checked)}
                  />
                </GridColumn>
              </GridRow>
              <GridRow headline={t('common.timeFrame')}>
                <GridColumn width={12}>
                  <GridRow spacingBottom='s'>
                    <GridColumn width={3}>
                      <DatePicker
                        disabled={!!values.rrule}
                        name='startDate'
                        value={values.startDate.toDate()}
                        label={t('common.from')}
                        onChange={async (e) => {
                          const newDate = dayjs(e);
                          const updated = values.startDate
                            .year(newDate.year())
                            .month(newDate.month())
                            .date(newDate.date());
                          await setFieldValue('startDate', updated);
                        }}
                        minDate={dayjs(currentSchoolYear?.start).toDate()}
                        maxDate={dayjs(currentSchoolYear?.end).toDate()}
                        error={errors.startDate as string}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <Input
                        name='startTime'
                        label={t('common.start')}
                        value={values.startDate.format('HH:mm')}
                        onChange={async (event: ChangeEvent<HTMLInputElement>) => {
                          const [hour, minute] = event.target.value.split(':').map(Number);
                          const updated = values.startDate.hour(hour).minute(minute);
                          await setFieldValue('startDate', updated);
                        }}
                        type='time'
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <DatePicker
                        disabled={!!values.rrule}
                        name='endDate'
                        value={values.endDate.toDate()}
                        label={t('common.until')}
                        onChange={async (e) => {
                          const newDate = dayjs(e);
                          const updated = values.endDate
                            .year(newDate.year())
                            .month(newDate.month())
                            .date(newDate.date());
                          await setFieldValue('endDate', updated);
                        }}
                        minDate={dayjs(currentSchoolYear?.start).toDate()}
                        maxDate={dayjs(currentSchoolYear?.end).toDate()}
                        error={errors.endDate as string}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <Input
                        name='endTime'
                        label={t('common.end')}
                        value={dayjs(values.endDate).format('HH:mm')}
                        onChange={async (event: ChangeEvent<HTMLInputElement>) => {
                          const [hour, minute] = event.target.value.split(':').map(Number);
                          const updated = values.endDate.hour(hour).minute(minute);
                          await setFieldValue('endDate', updated);
                        }}
                        type='time'
                      />
                    </GridColumn>
                  </GridRow>
                  {isEdit && (
                    <GridRow spacingTop='s'>
                      <GridColumn width={12}>
                        <p className='readonly-blend'>
                          {isSeriesEdit ? `${t('schoolEvents.editSeriesHint')} ` : ''}
                          {t('schoolEvents.editHint')}
                        </p>
                      </GridColumn>
                    </GridRow>
                  )}
                  {/* einzelner Termin (auch aus einer Serie): keine Wiederholung */}
                  {(!isEdit || isSeriesEdit) && (
                    <GridRow spacingTop='s'>
                      <GridColumn width={12}>
                        <RecurringEventForm
                          key={`${values.startDate.toISOString()}_${values.endDate.toISOString()}`}
                          start={values.startDate.toDate()}
                          rrule={values.rrule}
                          excludeHolidays={values.excludeHolidays}
                          onExcludeHolidaysChange={(value) => {
                            setFieldValue('excludeHolidays', value);
                          }}
                          onChange={(rrule) => {
                            setFieldValue('rrule', rrule);
                          }}
                          allowRecurring={allowRecurring}
                          isRecurrenceAllowed={(frequency) => {
                            if (!frequency) {
                              return false;
                            }
                            if (values.startDate.isSame(values.endDate, 'day')) {
                              return true;
                            }
                            const daysDiff = getDaysDifference(values.startDate, values.endDate);
                            if (frequency >= 3) {
                              return daysDiff < 7;
                            }
                            if (frequency === 2) {
                              return daysDiff < 7;
                            }
                            if (frequency === 1) {
                              return daysDiff < 31;
                            }
                            return false;
                          }}
                        />
                      </GridColumn>
                    </GridRow>
                  )}
                </GridColumn>
              </GridRow>
            </Grid>
            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  onClose();
                  resetForm();
                },
              }}
              submitButton={{
                // eine Serie braucht eine Regel
                disabled: isSubmitting || !dirty || (isSeriesEdit && !values.rrule),
              }}
              errors={errors}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
